import type { SortKeys } from '~/components/AltAccommodationPopUp/src/constants';
import type { Accommodation } from '~/schemas/trip';
import type { AlternativeEntry } from './AlternativeAccommodationCard';

export type AlternativeAccommodationResponse = {
  currentAccommodation: Accommodation;
  alternativeAccommodation: AlternativeEntry[];
};

export type CurrentStationDistances = {
  distanceFromArrivalStationMeters?: number | null;
  distanceFromDepartureStationMeters?: number | null;
};

export const buildAccommodationList = (
  data: AlternativeAccommodationResponse,
  currentStationDistances?: CurrentStationDistances
): AlternativeEntry[] => {
  const currencyId =
    data.alternativeAccommodation[0]?.priceDifference.currencyId ??
    data.currentAccommodation.selectedRooms[0]?.retail.currencyId ??
    'GBP';

  const currentAlternative = data.alternativeAccommodation.find((alt) => alt.id === data.currentAccommodation.id);

  const current: AlternativeEntry = {
    ...data.currentAccommodation,
    priceDifference: { value: 0, currencyId },
    distanceFromArrivalStationMeters:
      currentAlternative?.distanceFromArrivalStationMeters ??
      currentStationDistances?.distanceFromArrivalStationMeters ??
      null,
    distanceFromDepartureStationMeters:
      currentAlternative?.distanceFromDepartureStationMeters ??
      currentStationDistances?.distanceFromDepartureStationMeters ??
      null
  };

  const others = data.alternativeAccommodation.filter((alt) => alt.id !== data.currentAccommodation.id);

  return [current, ...others];
};

export const stationDistanceMeters = (alt: AlternativeEntry) =>
  alt.distanceFromArrivalStationMeters ?? alt.distanceFromDepartureStationMeters ?? null;

const preferCurrentOnTie = (
  a: AlternativeEntry,
  b: AlternativeEntry,
  currentAccommodationId: number | undefined,
  comparison: number
) => {
  if (comparison !== 0 || currentAccommodationId === undefined) return comparison;
  if (a.id === currentAccommodationId) return -1;
  if (b.id === currentAccommodationId) return 1;
  return 0;
};

export const sortAccommodationList = (
  list: AlternativeEntry[],
  sortBy: SortKeys | undefined,
  currentAccommodationId?: number
) => {
  if (!sortBy) return list;

  return [...list].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return preferCurrentOnTie(a, b, currentAccommodationId, a.priceDifference.value - b.priceDifference.value);
      case 'price-desc':
        return preferCurrentOnTie(a, b, currentAccommodationId, b.priceDifference.value - a.priceDifference.value);
      case 'distance-asc':
      case 'distance-desc': {
        const distanceA = stationDistanceMeters(a);
        const distanceB = stationDistanceMeters(b);
        if (distanceA === null && distanceB === null) {
          return preferCurrentOnTie(a, b, currentAccommodationId, 0);
        }
        if (distanceA === null) return 1;
        if (distanceB === null) return -1;
        const comparison = sortBy === 'distance-asc' ? distanceA - distanceB : distanceB - distanceA;
        return preferCurrentOnTie(a, b, currentAccommodationId, comparison);
      }
      default:
        return preferCurrentOnTie(a, b, currentAccommodationId, 0);
    }
  });
};

export const filterAccommodationBySearch = (list: AlternativeEntry[], query: string) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return list;
  return list.filter((alt) => alt.title.toLowerCase().includes(normalized));
};
